import { useState, useCallback } from 'react'
import useToast from '@/hooks/useToast'
import ErrorToast from '@/components/ErrorToast'

/**
 * Custom hook for fetching paginated list data using Promise.all
 * Fetches all pages concurrently for better performance
 * 
 * @param {object} options - Configuration options
 * @param {number} options.defaultLimit - Default page size limit (default: 50)
 * @param {function} options.dataTransform - Optional function to transform the final data
 * @param {string} options.errorMessage - Custom error message for failed requests
 * @returns {object} Hook state and functions
 */
export default function usePaginatedList({
  defaultLimit = 50,
  dataTransform = null,
  errorMessage = 'Failed to fetch data'
} = {}) {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  const { toast } = useToast()

  /**
   * Fetches all paginated data using Promise.all
   * 
   * @param {function} apiFunction - API function to call for each page
   * @param {object} requestBody - Base request body (page will be overridden)
   * @param {number} limit - Items per page (optional, uses defaultLimit if not provided)
   * @returns {Promise<Array>} Array of all fetched data
   */
  const fetchPaginatedData = useCallback(async (apiFunction, requestBody = {}, limit = defaultLimit) => {
    if (!apiFunction) {
      throw new Error('API function is required')
    }

    let allData = []
    let total = 0
    setIsLoading(true)
    setError(null)
    
    try {
      // First request to get total count
      const firstResponse = await apiFunction({
        ...requestBody,
        page: 1,
        limit,
      })

      if (firstResponse?.status >= 400) {
        const errorMsg = firstResponse?.error?.message || errorMessage
        toast.custom(t => <ErrorToast t={t} error={errorMsg} />)
        setError(errorMsg)
        return []
      }

      // Add first page data
      allData.push(...(firstResponse.data?.data || []))
      total = firstResponse.data?.metadata?.pagination?.total || 0

      // Calculate remaining pages
      const totalPages = Math.ceil(total / limit)
      const remainingPages = totalPages - 1

      if (remainingPages > 0) {
        // Fetch all remaining pages concurrently using Promise.all
        const additionalRequests = Array.from(
          { length: remainingPages },
          (_, idx) => apiFunction({
            ...requestBody,
            page: idx + 2, // Start from page 2
            limit,
          })
        )

        const additionalResponses = await Promise.all(additionalRequests)

        // Process each response
        additionalResponses.forEach((response, idx) => {
          if (response?.status >= 400) {
            const errorMsg = response?.error?.message || `${errorMessage} (page ${idx + 2})`
            toast.custom(t => <ErrorToast t={t} error={errorMsg} />)
          } else {
            allData.push(...(response.data?.data || []))
          }
        })
      }

      // Apply data transformation if provided
      const transformedData = dataTransform ? dataTransform(allData) : allData
      
      setData(transformedData)
      return transformedData

    } catch (err) {
      const errorMsg = err?.message || errorMessage
      toast.custom(t => <ErrorToast t={t} error={errorMsg} />)
      setError(errorMsg)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [defaultLimit, dataTransform, errorMessage, toast])

  /**
   * Resets the hook state
   */
  const reset = useCallback(() => {
    setData([])
    setError(null)
    setIsLoading(false)
  }, [])

  return {
    data,
    isLoading,
    error,
    fetchPaginatedData,
    reset,
    // Computed values
    hasData: data.length > 0,
    isEmpty: !isLoading && data.length === 0,
  }
}
